import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Stack,
  Box,
  CircularProgress
} from '@mui/material';
import { SupplierAddress } from '@domain/suppliers/models/Supplier';
import { useSupplier } from '../../hooks/useSupplier';
import { useToast } from '@shared/hooks/useToast';

interface AddAddressDialogProps {
  open: boolean;
  onClose: () => void;
  supplierId: string;
  currentSupplier: any;
}

interface AddressFormData {
  type: 'office' | 'production' | 'warehouse' | 'billing';
  name: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  isPrimary: boolean;
}

const AddAddressDialog: React.FC<AddAddressDialogProps> = ({
  open,
  onClose,
  supplierId,
  currentSupplier
}) => {
  const { addAddress, isAddingAddress } = useSupplier(supplierId);
  const { success, error } = useToast();
  
  const [formData, setFormData] = useState<AddressFormData>({
    type: 'office',
    name: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'USA',
    isPrimary: false
  });

  const handleInputChange = (field: keyof AddressFormData, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name.trim() || !formData.address.trim() || !formData.city.trim()) {
      return;
    }

    try {
      await addAddress({
        supplierId,
        address: formData,
        currentSupplier
      });
      
      success('Address added successfully');
      
      // Reset form and close
      setFormData({
        type: 'office',
        name: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'USA',
        isPrimary: false
      });
      onClose();
    } catch (err) {
      error('Failed to add address');
    }
  };

  const handleClose = () => {
    // Reset form
    setFormData({
      type: 'office',
      name: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'USA',
      isPrimary: false
    });
    onClose();
  };

  const isFormValid = formData.name.trim() && formData.address.trim() && formData.city.trim();

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Address</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <FormControl fullWidth>
            <InputLabel>Address Type</InputLabel>
            <Select
              value={formData.type}
              label="Address Type"
              onChange={(e) => handleInputChange('type', e.target.value as any)}
            >
              <MenuItem value="office">Office</MenuItem>
              <MenuItem value="production">Production</MenuItem>
              <MenuItem value="warehouse">Warehouse</MenuItem>
              <MenuItem value="billing">Billing</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Address Name"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            placeholder="e.g., Main Office, Manufacturing Facility"
            required
          />

          <TextField
            fullWidth
            label="Street Address"
            value={formData.address}
            onChange={(e) => handleInputChange('address', e.target.value)}
            placeholder="123 Business Street"
            required
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              label="City"
              value={formData.city}
              onChange={(e) => handleInputChange('city', e.target.value)}
              placeholder="San Francisco"
              required
            />
            <TextField
              fullWidth
              label="State"
              value={formData.state}
              onChange={(e) => handleInputChange('state', e.target.value)}
              placeholder="CA"
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              label="ZIP Code"
              value={formData.zipCode}
              onChange={(e) => handleInputChange('zipCode', e.target.value)}
              placeholder="94105"
            />
            <TextField
              fullWidth
              label="Country"
              value={formData.country}
              onChange={(e) => handleInputChange('country', e.target.value)}
              placeholder="USA"
            />
          </Box>

          <FormControlLabel
            control={
              <Switch
                checked={formData.isPrimary}
                onChange={(e) => handleInputChange('isPrimary', e.target.checked)}
                color="primary"
              />
            }
            label="Set as primary address"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
          disabled={!isFormValid || isAddingAddress}
          startIcon={isAddingAddress ? <CircularProgress size={16} /> : undefined}
        >
          {isAddingAddress ? 'Adding...' : 'Add Address'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddAddressDialog; 